import { useState, useRef, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useRouterState, useNavigate } from '@tanstack/react-router';
import {
  LayoutDashboard,
  Package,
  FolderOpen,
  Activity,
  PlusCircle,
  Images,
  BookOpen,
  Info,
  Eye,
  Shield,
  Settings2,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  Globe,
  Monitor,
  Heart,
  Handshake,
  User,
  BarChart3,
  Rss,
  MessageSquare,
  MessageSquareText,
  Copy,
  Target,
  Share2,
  Sparkles,
  Vault,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { usePreferences } from '../../hooks/usePreferences';
import { useTranslation } from '../../i18n';
import type { TranslationKey } from '../../i18n';
import { useAppMode } from '../../context/AppModeContext';
import { useVault } from '../../context/VaultContext';
import { FeatureDisabledBanner } from '../FeatureDisabledBanner';
import type { Locale } from '../../i18n';
import { THEMES, THEME_DEFINITIONS, type ThemeGroup } from '@siliconvault/shared';
import { KOFI_URL } from '../../constants/external';
import { NotificationBell } from './NotificationBell';
import { ShareCatalogModal } from '../community/ShareCatalogModal';
import styles from './Shell.module.css';

interface ShellProps {
  children: ReactNode;
}

interface NavSection {
  label: string;
  items: Array<{ path: string; label: string; Icon: LucideIcon }>;
  adminOnly?: boolean;
}

// Community routes that are gated in desktop mode
const COMMUNITY_PATHS = ['/showroom', '/wanted', '/offers', '/profile/'];

function useNavSections(communityEnabled: boolean, isDesktop: boolean, vaultFeatures?: Record<string, boolean>): NavSection[] {
  const { t } = useTranslation();

  // Build collection items, conditionally including feature-gated sections
  const collectionItems: Array<{ path: string; label: string; Icon: LucideIcon }> = [
    { path: '/dashboard', label: t('nav.dashboard'), Icon: LayoutDashboard },
    { path: '/inventory', label: t('nav.inventory'), Icon: Package },
    { path: '/inventory/new', label: t('nav.addItem'), Icon: PlusCircle },
    { path: '/inventory/batch', label: t('nav.batchAdd'), Icon: Images },
  ];

  // Library is always available
  collectionItems.push({ path: '/library', label: t('nav.library'), Icon: FolderOpen });

  // Health/Maintenance — show if vault has maintenance-related features or always show by default
  collectionItems.push({ path: '/health', label: t('nav.health'), Icon: Activity });

  collectionItems.push(
    { path: '/stats', label: t('nav.stats'), Icon: BarChart3 },
    { path: '/duplicates', label: t('nav.duplicates'), Icon: Copy },
    { path: '/sets', label: t('nav.sets'), Icon: Target },
  );

  const sections: NavSection[] = [
    {
      label: t('nav.collection'),
      items: collectionItems,
    },
    {
      label: t('nav.reference'),
      items: [
        { path: '/guide', label: t('nav.userGuide'), Icon: BookOpen },
        { path: '/about', label: t('nav.about'), Icon: Info },
      ],
    },
  ];

  // Community section only shown when enabled (web mode)
  if (communityEnabled) {
    sections.splice(1, 0, {
      label: t('nav.community'),
      items: [
        { path: '/showroom', label: t('nav.showroom'), Icon: Eye },
        { path: '/wanted', label: t('nav.wanted'), Icon: Heart },
        { path: '/offers', label: t('nav.offers'), Icon: Handshake },
        { path: '/messages', label: t('nav.messages'), Icon: MessageSquare },
      ],
    });
  }

  return sections;
}

function useBreadcrumbMap(): Record<string, Array<{ label: string; href?: string }>> {
  const { t } = useTranslation();
  return {
    '/dashboard': [{ label: t('breadcrumb.dashboard') }],
    '/inventory': [{ label: t('breadcrumb.inventory') }],
    '/showroom': [{ label: t('breadcrumb.showroom') }],
    '/wanted': [{ label: t('breadcrumb.wanted') }],
    '/offers': [{ label: t('breadcrumb.offers') }],
    '/inventory/new': [{ label: t('breadcrumb.inventory'), href: '/inventory' }, { label: t('breadcrumb.newItem') }],
    '/inventory/batch': [{ label: t('breadcrumb.inventory'), href: '/inventory' }, { label: t('breadcrumb.batchAdd') }],
    '/library': [{ label: t('breadcrumb.library') }],
    '/health': [{ label: t('breadcrumb.health') }],
    '/settings': [{ label: t('breadcrumb.settings') }],
    '/guide': [{ label: t('breadcrumb.userGuide') }],
    '/about': [{ label: t('nav.about') }],
    '/terms': [{ label: t('footer.terms') }],
    '/privacy': [{ label: t('footer.privacy') }],
    '/stats': [{ label: t('breadcrumb.stats') }],
    '/activity': [{ label: t('breadcrumb.activity') }],
    '/messages': [{ label: t('breadcrumb.messages') }],
    '/duplicates': [{ label: t('breadcrumb.duplicates') }],
    '/sets': [{ label: t('breadcrumb.sets') }],
    '/feedback': [{ label: t('nav.feedback') }],
    '/admin': [{ label: t('breadcrumb.admin') }],
    '/upgrade': [{ label: t('upgrade.title') }],
  };
}

function NavLink({
  path,
  label,
  Icon,
  currentPath,
  onClick,
  disabled,
}: {
  path: string;
  label: string;
  Icon: LucideIcon;
  currentPath: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  const isActive =
    currentPath === path ||
    (path === '/inventory' &&
      currentPath.startsWith('/inventory') &&
      currentPath !== '/inventory/new' &&
      currentPath !== '/inventory/batch');

  return (
    <Link
      to={disabled ? '/vaults' : path}
      className={`${styles.navLink} ${isActive ? styles.navLinkActive : ''}${disabled ? ` ${styles.navLinkDisabled}` : ''}`}
      onClick={onClick}
    >
      <span className={styles.navIcon}>
        <Icon size={20} strokeWidth={1.75} />
      </span>
      {label}
    </Link>
  );
}

export function Shell({ children }: ShellProps) {
  const { logout, user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const { t, locale } = useTranslation();
  const { preferences, setPreference } = usePreferences();
  const { communityEnabled, mode, aiTier } = useAppMode();
  const { activeVault, activeVaultId, vaultTypeDef, multiVaultEnabled, vaults, setActiveVaultId } = useVault();
  const isDesktop = mode === 'desktop';
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const userChipRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const themeToggleRef = useRef<HTMLButtonElement>(null);
  const themeDropdownRef = useRef<HTMLDivElement>(null);
  const [vaultMenuOpen, setVaultMenuOpen] = useState(false);
  const vaultToggleRef = useRef<HTMLButtonElement>(null);
  const vaultDropdownRef = useRef<HTMLDivElement>(null);
  const [vaultMenuStyle, setVaultMenuStyle] = useState<React.CSSProperties>({});
  const [userMenuStyle, setUserMenuStyle] = useState<React.CSSProperties>({});
  const [themeMenuStyle, setThemeMenuStyle] = useState<React.CSSProperties>({});

  const vaultFeatures = vaultTypeDef?.specializedFeatures as Record<string, boolean> | undefined;
  const navSections = useNavSections(communityEnabled, isDesktop, vaultFeatures);
  const breadcrumbMap = useBreadcrumbMap();

  // Position and close dropdown on outside click
  const updateUserMenuPos = useCallback(() => {
    if (userChipRef.current) {
      const rect = userChipRef.current.getBoundingClientRect();
      const isRtl = document.documentElement.dir === 'rtl';
      const menuWidth = 160;
      if (isRtl) {
        const left = Math.max(8, Math.min(rect.left, window.innerWidth - menuWidth - 8));
        setUserMenuStyle({ top: rect.bottom + 6, left, right: 'auto' });
      } else {
        const right = Math.max(8, window.innerWidth - rect.right);
        setUserMenuStyle({ top: rect.bottom + 6, right, left: 'auto' });
      }
    }
  }, []);

  useEffect(() => {
    if (!userMenuOpen) return;
    updateUserMenuPos();
    function handleClickOutside(e: MouseEvent) {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        userChipRef.current && !userChipRef.current.contains(e.target as Node)
      ) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen, updateUserMenuPos]);

  // Position theme dropdown and close on outside click
  const updateThemeMenuPos = useCallback(() => {
    if (themeToggleRef.current) {
      const rect = themeToggleRef.current.getBoundingClientRect();
      const isRtl = document.documentElement.dir === 'rtl';
      const menuWidth = 180;
      if (isRtl) {
        const left = Math.max(8, Math.min(rect.left, window.innerWidth - menuWidth - 8));
        setThemeMenuStyle({ top: rect.bottom + 6, left, right: 'auto' });
      } else {
        const right = Math.max(8, window.innerWidth - rect.right);
        setThemeMenuStyle({ top: rect.bottom + 6, right, left: 'auto' });
      }
    }
  }, []);

  useEffect(() => {
    if (!themeMenuOpen) return;
    updateThemeMenuPos();
    function handleClickOutside(e: MouseEvent) {
      if (
        themeDropdownRef.current && !themeDropdownRef.current.contains(e.target as Node) &&
        themeToggleRef.current && !themeToggleRef.current.contains(e.target as Node)
      ) {
        setThemeMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [themeMenuOpen, updateThemeMenuPos]);

  // Vault dropdown positioning + outside click
  const updateVaultMenuPos = useCallback(() => {
    if (vaultToggleRef.current) {
      const rect = vaultToggleRef.current.getBoundingClientRect();
      const isRtl = document.documentElement.dir === 'rtl';
      const menuWidth = 220;
      if (isRtl) {
        const left = Math.max(8, Math.min(rect.left, window.innerWidth - menuWidth - 8));
        setVaultMenuStyle({ top: rect.bottom + 6, left, right: 'auto' });
      } else {
        const right = Math.max(8, window.innerWidth - rect.right);
        setVaultMenuStyle({ top: rect.bottom + 6, right, left: 'auto' });
      }
    }
  }, []);

  useEffect(() => {
    if (!vaultMenuOpen) return;
    updateVaultMenuPos();
    function handleClickOutside(e: MouseEvent) {
      if (
        vaultDropdownRef.current && !vaultDropdownRef.current.contains(e.target as Node) &&
        vaultToggleRef.current && !vaultToggleRef.current.contains(e.target as Node)
      ) {
        setVaultMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [vaultMenuOpen, updateVaultMenuPos]);

  // Automatically switch theme when user explicitly switches vaults.
  // Skip on initial vault selection (prevVaultIdRef starts null) so new
  // users always land on their saved theme (modern_dark by default).
  const prevVaultIdRef = useRef(activeVaultId);
  useEffect(() => {
    if (activeVaultId && prevVaultIdRef.current !== activeVaultId) {
      const wasInitialSelection = prevVaultIdRef.current === null;
      prevVaultIdRef.current = activeVaultId;
      if (wasInitialSelection) return;
      const defaultThemes = vaultTypeDef?.defaultThemes;
      if (defaultThemes && defaultThemes.length > 0) {
        if (!defaultThemes.includes(preferences.theme)) {
          setPreference('theme', defaultThemes[0]);
        }
      }
    }
  }, [activeVaultId, vaultTypeDef?.defaultThemes, preferences.theme, setPreference]);

  // Auth guard — navigate in effect so it only fires after state commits,
  // avoiding a race condition where Shell renders before setUser propagates.
  // On desktop, never redirect to landing — we show loading/error states instead.
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !isDesktop) {
      navigate({ to: '/' });
    }
  }, [isLoading, isAuthenticated, navigate, isDesktop]);

  // Elevate archetype class to document.body so React Portals (dropdowns/modals) inherit theme styles.
  // MUST be before any early returns to satisfy Rules of Hooks.
  const themeGroup = THEME_DEFINITIONS[preferences.theme]?.group ?? 'General';
  let archetype = '';
  if (themeGroup === 'Retro Computers') archetype = 'terminal';
  else if (themeGroup === 'Watches') archetype = 'elegant';
  else if (themeGroup === 'Vintage Cameras' || themeGroup === 'Retro Audio / Hi-Fi') archetype = 'analog';

  useEffect(() => {
    document.body.classList.remove('archetype-terminal', 'archetype-elegant', 'archetype-analog');
    if (archetype) {
      document.body.classList.add(`archetype-${archetype}`);
    }
  }, [archetype]);

  if (isLoading) {
    if (isDesktop) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', height: '100vh', gap: '16px',
          background: 'var(--bg-primary)', color: 'var(--text-primary)',
          fontFamily: 'var(--font-mono, monospace)',
        }}>
          <div style={{
            width: 32, height: 32, border: '2px solid var(--accent)',
            borderTopColor: 'transparent', borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <span style={{ fontSize: '0.875rem', opacity: 0.7 }}>Starting up...</span>
        </div>
      );
    }
    return null;
  }

  if (!isAuthenticated) {
    if (isDesktop) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', height: '100vh', gap: '12px',
          background: 'var(--bg-primary)', color: 'var(--text-primary)',
          fontFamily: 'var(--font-mono, monospace)', textAlign: 'center', padding: '24px',
        }}>
          <span style={{ fontSize: '1.5rem' }}>⚠</span>
          <p style={{ margin: 0, fontWeight: 600 }}>Server failed to start</p>
          <p style={{ margin: 0, fontSize: '0.875rem', opacity: 0.7 }}>
            Please restart SiliconVault.
          </p>
        </div>
      );
    }
    return null;
  }

  function toggleLocale() {
    const next: Locale = locale === 'en' ? 'he' : 'en';
    setPreference('locale', next);
  }

  function getBreadcrumbs(path: string) {
    if (breadcrumbMap[path]) return breadcrumbMap[path];
    if (path.startsWith('/inventory/')) {
      const searchParams = new URLSearchParams(routerState.location.search);
      if (searchParams.get('from') === 'showroom') {
        return [{ label: t('breadcrumb.showroom'), href: '/showroom' }, { label: t('breadcrumb.itemDetail') }];
      }
      return [{ label: t('breadcrumb.inventory'), href: '/inventory' }, { label: t('breadcrumb.itemDetail') }];
    }
    if (path.startsWith('/sets/')) {
      return [{ label: t('breadcrumb.sets'), href: '/sets' }, { label: t('breadcrumb.setDetail') }];
    }
    if (path.startsWith('/profile/')) {
      return [{ label: t('breadcrumb.showroom'), href: '/showroom' }, { label: t('breadcrumb.profile') }];
    }
    if (path.startsWith('/offers/')) {
      return [{ label: t('breadcrumb.offers'), href: '/offers' }, { label: t('breadcrumb.offerDetail') }];
    }
    return [];
  }

  const breadcrumbs = getBreadcrumbs(currentPath);
  const initials = user?.displayName?.slice(0, 2).toUpperCase() ?? '??';

  // Collection nav items require at least one vault
  const collectionLabel = t('nav.collection');
  const noVault = multiVaultEnabled && vaults.length === 0;

  const navContent = (onClick?: () => void) => (
    <>
      {navSections.map((section) => {
        if (section.adminOnly && user?.role !== 'admin') return null;
        const isCollection = section.label === collectionLabel;
        return (
          <div key={section.label} className={styles.navGroup}>
            <div className={styles.navGroupLabel}>{section.label}</div>
            {section.items.map(({ path, label, Icon }) => (
              <NavLink
                key={path}
                path={path}
                label={label}
                Icon={Icon}
                currentPath={currentPath}
                onClick={onClick}
                disabled={isCollection && noVault}
              />
            ))}
          </div>
        );
      })}
      {!isDesktop && (
        <div className={styles.navGroupBottom}>
          <Link
            to="/upgrade"
            className={`${styles.navLink} ${currentPath === '/upgrade' ? styles.navLinkActive : ''}`}
            onClick={onClick}
          >
            <span className={styles.navIcon}>
              <Sparkles size={16} strokeWidth={1.75} />
            </span>
            {aiTier === 'pro' ? t('upgrade.sidebarBadgePro') : t('upgrade.sidebarBadge')}
          </Link>
        </div>
      )}
    </>
  );

  return (
    <div className={`${styles.shell} ${archetype ? `archetype-${archetype}` : ''}`}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button
            className={styles.hamburger}
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={20} strokeWidth={1.75} />
          </button>
          <Link to="/dashboard" className={styles.logo}>
            <img src="/icon.svg" alt="" className={styles.logoIcon} />
            <span className={styles.logoText}>SiliconVault</span>
          </Link>
        </div>

        <div className={styles.headerRight}>
          {/* Theme dropdown */}
          <button
            ref={themeToggleRef}
            className={styles.themeToggle}
            onClick={() => setThemeMenuOpen(!themeMenuOpen)}
            title={t('nav.switchTheme')}
          >
            <Monitor size={14} strokeWidth={1.75} />
            <span
              style={{
                position: 'absolute',
                bottom: 3,
                right: 3,
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: THEME_DEFINITIONS[preferences.theme]?.colors['--accent'] ?? '#00ff41',
              }}
            />
          </button>

          {themeMenuOpen && createPortal(
            <div ref={themeDropdownRef} className={styles.themeDropdown} style={themeMenuStyle}>
              {(() => {
                // Show vault-specific themes + General; fall back to all if no vault
                const vaultThemes = vaultTypeDef?.defaultThemes as typeof THEMES[number][] | undefined;
                const generalIds = THEMES.filter((id) => THEME_DEFINITIONS[id].group === 'General');
                const visibleIds = vaultThemes
                  ? [...vaultThemes, ...generalIds.filter((id) => !vaultThemes.includes(id))]
                  : generalIds;

                const groupOrder: ThemeGroup[] = ['Retro Computers', 'Retro Audio / Hi-Fi', 'Vintage Cameras', 'Watches', 'General'];
                const grouped = new Map<ThemeGroup, typeof THEMES[number][]>();
                for (const id of visibleIds) {
                  const g = THEME_DEFINITIONS[id].group;
                  if (!grouped.has(g)) grouped.set(g, []);
                  grouped.get(g)!.push(id);
                }

                return groupOrder.filter((g) => grouped.has(g)).map((g) => {
                  const group = { label: g, themes: grouped.get(g)! };
                  return (
                  <div key={group.label}>
                    <div className={styles.themeGroupLabel}>{group.label}</div>
                    {group.themes.map((id) => {
                      const def = THEME_DEFINITIONS[id];
                      return (
                        <button
                          key={id}
                          className={`${styles.themeDropdownItem} ${preferences.theme === id ? styles.themeDropdownItemActive : ''}`}
                          onClick={() => { setPreference('theme', id); setThemeMenuOpen(false); }}
                        >
                          <span
                            className={styles.themeDot}
                            style={{ background: def.colors['--bg-primary'], border: `2px solid ${def.colors['--accent']}` }}
                          />
                          {def.label}
                        </button>
                      );
                    })}
                  </div>
                  );
                });
              })()}
            </div>,
            document.body
          )}

          {/* Language toggle */}
          <button
            className={styles.langToggle}
            onClick={toggleLocale}
            title={locale === 'en' ? 'עברית' : 'English'}
          >
            <Globe size={14} strokeWidth={1.75} />
            <span>{locale === 'en' ? 'EN' : 'עב'}</span>
          </button>

          {/* Notifications */}
          <NotificationBell />

          {/* Vault selector dropdown */}
          {multiVaultEnabled && activeVault && (
            <>
              <button
                ref={vaultToggleRef}
                className={styles.vaultChip}
                onClick={() => setVaultMenuOpen(!vaultMenuOpen)}
                title={t('vaults.switchVault')}
              >
                <span className={styles.vaultChipIcon}>
                  <Vault size={12} strokeWidth={2} />
                </span>
                <span className={styles.vaultChipName}>{activeVault.customDefinition?.displayName ?? t(`vaults.type.${activeVault.vaultType}` as TranslationKey)}</span>
                <ChevronDown size={12} className={`${styles.userChevron} ${vaultMenuOpen ? styles.userChevronOpen : ''}`} />
              </button>

              {vaultMenuOpen && createPortal(
                <div ref={vaultDropdownRef} className={styles.vaultDropdown} style={vaultMenuStyle}>
                  {vaults.map((v) => (
                    <button
                      key={v.id}
                      className={`${styles.dropdownItem} ${v.id === activeVaultId ? styles.vaultDropdownItemActive : ''}`}
                      onClick={() => {
                        setActiveVaultId(v.id);
                        setVaultMenuOpen(false);
                      }}
                    >
                      {v.customDefinition?.displayName ?? t(`vaults.type.${v.vaultType}` as TranslationKey)}
                    </button>
                  ))}
                  {activeVault.customDefinition && (
                    <>
                      <div className={styles.dropdownDivider} />
                      <Link
                        to="/vaults/settings"
                        className={styles.dropdownItem}
                        onClick={() => setVaultMenuOpen(false)}
                      >
                        <Settings2 size={14} strokeWidth={1.75} />
                        {t('vaults.settings.navLabel' as TranslationKey)}
                      </Link>
                    </>
                  )}
                  <div className={styles.dropdownDivider} />
                  <Link
                    to="/vaults"
                    className={styles.dropdownItem}
                    onClick={() => setVaultMenuOpen(false)}
                  >
                    {t('vaults.manageVaults')}
                  </Link>
                </div>,
                document.body
              )}
            </>
          )}

          {/* User dropdown */}
          {user && (
            <>
              <button
                ref={userChipRef}
                className={styles.userChip}
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                <span className={styles.userAvatar}>{initials}</span>
                <span className={styles.userName}>{user.displayName}</span>
                <ChevronDown size={12} className={`${styles.userChevron} ${userMenuOpen ? styles.userChevronOpen : ''}`} />
              </button>

              {userMenuOpen && createPortal(
                <div ref={dropdownRef} className={styles.userDropdown} style={userMenuStyle}>
                  <Link
                    to="/settings"
                    className={styles.dropdownItem}
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <Settings2 size={14} strokeWidth={1.75} />
                    {t('nav.settings')}
                  </Link>
                  {!isDesktop && (
                    <Link
                      to="/profile/$slug"
                      params={{ slug: user?.profileSlug || user?.userId || '' }}
                      className={styles.dropdownItem}
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <User size={14} strokeWidth={1.75} />
                      {t('nav.myProfile')}
                    </Link>
                  )}
                  {!isDesktop && (
                    <button
                      className={styles.dropdownItem}
                      onClick={() => { setUserMenuOpen(false); setShowShareModal(true); }}
                    >
                      <Share2 size={14} strokeWidth={1.75} />
                      {t('nav.shareCatalog')}
                    </button>
                  )}
                  {!isDesktop && (
                    <Link
                      to="/feedback"
                      className={styles.dropdownItem}
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <MessageSquareText size={14} strokeWidth={1.75} />
                      {t('nav.feedback')}
                    </Link>
                  )}
                  {!isDesktop && user?.role === 'admin' && (
                    <>
                      <div className={styles.dropdownDivider} />
                      <Link
                        to="/admin"
                        className={styles.dropdownItem}
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Shield size={14} strokeWidth={1.75} />
                        {t('nav.adminPanel')}
                      </Link>
                    </>
                  )}
                  {!isDesktop && (
                    <>
                      <div className={styles.dropdownDivider} />
                      <button
                        className={styles.dropdownItem}
                        onClick={() => { setUserMenuOpen(false); logout(); }}
                      >
                        <LogOut size={14} strokeWidth={1.75} />
                        {t('nav.logout')}
                      </button>
                    </>
                  )}
                </div>,
                document.body
              )}
            </>
          )}
        </div>
      </header>

      {/* Desktop sidebar */}
      <nav className={styles.sidebar}>{navContent()}</nav>

      {/* Mobile overlay + drawer */}
      {mobileOpen && (
        <div className={styles.mobileOverlay} onClick={() => setMobileOpen(false)}>
          <nav
            className={styles.mobileDrawer}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.drawerHeader}>
              <span className={styles.drawerLogo}>
                <img src="/icon.svg" alt="" className={styles.drawerLogoIcon} />
                SiliconVault
              </span>
              <button
                className={styles.drawerClose}
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
              >
                <X size={20} strokeWidth={1.75} />
              </button>
            </div>
            {navContent(() => setMobileOpen(false))}
          </nav>
        </div>
      )}

      {/* Main */}
      <main className={styles.main} style={preferences.showTicker ? { paddingBottom: '28px' } : undefined}>
        {breadcrumbs.length > 0 && (
          <nav className={styles.breadcrumbs} aria-label="Breadcrumb">
            {breadcrumbs.map((crumb, i) => (
              <span key={i} className={styles.breadcrumbItem}>
                {i > 0 && <ChevronRight size={12} className={styles.breadcrumbSep} />}
                {crumb.href ? (
                  <Link to={crumb.href} className={styles.breadcrumbLink}>
                    {crumb.label}
                  </Link>
                ) : (
                  <span className={styles.breadcrumbCurrent}>{crumb.label}</span>
                )}
              </span>
            ))}
          </nav>
        )}
        <div className={styles.mainContent}>
          {!communityEnabled && COMMUNITY_PATHS.some((p) => currentPath.startsWith(p))
            ? <FeatureDisabledBanner feature="community" className={styles.communityBanner} />
            : children}
        </div>
        {showShareModal && <ShareCatalogModal onClose={() => setShowShareModal(false)} />}
        <footer className={styles.footer}>
          © {new Date().getFullYear()} SiliconVault. {t('footer.rights')} {t('footer.builtBy')}
          {aiTier !== 'pro' && (
            <>
              <span className={styles.footerSep}> | </span>
              <Link to="/about" className={styles.footerSupport}>
                <Heart size={10} strokeWidth={2} />
                {t('support.footerLink')}
              </Link>
            </>
          )}
          <span className={styles.footerSep}> | </span>
          <Link to="/terms" className={styles.footerLink}>{t('footer.terms')}</Link>
          <span className={styles.footerSep}> | </span>
          <Link to="/privacy" className={styles.footerLink}>{t('footer.privacy')}</Link>
        </footer>
      </main>
    </div>
  );
}
